import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import GridModeUrlBuilder from './GridModeUrlBuilder.jsx';
import DroppedValuesNotice from './gridModeUrlBuilder/DroppedValuesNotice.jsx';
import SnapshotStatus from './gridModeUrlBuilder/SnapshotStatus.jsx';
import {
  readSnapshot, writeSnapshot, removeSnapshot, sweepExpiredSnapshots,
  isStorageAvailable, readAutosavePreference, writeAutosavePreference,
} from './gridModeUrlBuilder/gridSnapshotStore';
import { reconcileSnapshot, stripDerivedCells, hasContent } from './gridModeUrlBuilder/reconcileSnapshot';
import { gridModeUrlBuilderPropTypes } from './gridModeUrlBuilder/propTypes';

export const SAVE_DEBOUNCE_MS = 500;

const CONFIRM_LOAD = 'Replace the grid on screen with the saved grid? Your unsaved changes will be lost.';

// The stored grid reconciled with the current columns, or null when there is
// none worth loading.
const readStoredGrid = (snapshotKey, columns) => {
  const snapshot = readSnapshot(snapshotKey);
  if (!snapshot) return null;
  const reconciled = reconcileSnapshot(snapshot, columns);
  if (!hasContent(reconciled, columns)) return null;
  return { savedAt: snapshot.savedAt, reconciled };
};

// Keeps the Grid Mode grid in the browser and lets the user load it whenever
// one is saved (#2633). Loading remounts the grid with the snapshot as its initial data:
// the same path that repopulates the grid after a failed save.
//
// Autosave (default) writes every change after a debounce. With autosave off
// the stored grid is a checkpoint: only "Save grid" writes it.
export default function GridModeUrlBuilderWithSnapshot({
  snapshot_key: snapshotKey,
  url_columns: urlColumns,
  field_columns: fieldColumns,
  other_columns: otherColumns,
  initial_row_data: initialRowData,
  initial_pinned_row_data: initialPinnedRowData,
}) {
  const columns = useMemo(
    () => [...urlColumns, ...fieldColumns, ...otherColumns],
    [urlColumns, fieldColumns, otherColumns],
  );

  const [storageAvailable] = useState(isStorageAvailable);
  // After a failed save the server already refilled the grid: nothing is
  // offered, and that grid is not in storage yet, so it starts out unsaved.
  const refilled = initialRowData.length > 0;
  const [offer, setOffer] = useState(
    () => (refilled ? null : readStoredGrid(snapshotKey, columns)),
  );
  const [loaded, setLoaded] = useState(null);
  const [loadCount, setLoadCount] = useState(0);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [autosave, setAutosave] = useState(readAutosavePreference);
  const [saved, setSaved] = useState(() => {
    const grid = refilled ? readStoredGrid(snapshotKey, columns) : offer;
    return grid && { savedAt: grid.savedAt, rowCount: grid.reconciled.rowData.length };
  });
  const [dirty, setDirty] = useState(refilled);

  useEffect(() => {
    sweepExpiredSnapshots();
  }, []);

  // Refs so the callbacks handed to the grid and window keep a stable identity.
  const autosaveRef = useRef(autosave);
  const dirtyRef = useRef(refilled);
  const submittingRef = useRef(false);
  // onGridChanged never fires for initial data, so seed the refilled grid:
  // Save grid and the autosave toggle need it before the first edit.
  const latestGrid = useRef(
    refilled ? { rowData: initialRowData, pinnedRowData: initialPinnedRowData } : null,
  );
  const pendingWrite = useRef(null);
  const writeTimer = useRef(null);

  const markDirty = useCallback((value) => {
    dirtyRef.current = value;
    setDirty(value);
  }, []);

  const write = useCallback((grid) => {
    const snapshot = stripDerivedCells(grid, columns);
    if (writeSnapshot(snapshotKey, snapshot)) {
      setSaved({ savedAt: new Date().toISOString(), rowCount: snapshot.rowData.length });
      return true;
    }
    // writeSnapshot removed the older entry, so nothing is saved any more.
    setSaved(null);
    return false;
  }, [snapshotKey, columns]);

  const cancelPendingWrite = useCallback(() => {
    if (writeTimer.current) {
      clearTimeout(writeTimer.current);
      writeTimer.current = null;
    }
  }, []);

  const dropPendingWrite = useCallback(() => {
    cancelPendingWrite();
    pendingWrite.current = null;
  }, [cancelPendingWrite]);

  const flush = useCallback(() => {
    cancelPendingWrite();
    if (pendingWrite.current) {
      if (write(pendingWrite.current)) markDirty(false);
      pendingWrite.current = null;
    }
  }, [cancelPendingWrite, write, markDirty]);

  const scheduleWrite = useCallback((grid) => {
    pendingWrite.current = grid;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // With autosave on the stored grid follows the screen, so a refilled grid is
  // written like any edit. With autosave off it waits for Save grid.
  useEffect(() => {
    if (refilled && autosaveRef.current) scheduleWrite(latestGrid.current);
    // Mount only: the initial props describe the refilled grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGridChanged = useCallback((grid) => {
    setOffer(null);
    latestGrid.current = grid;
    if (!autosaveRef.current) {
      dirtyRef.current = true;
      setDirty(true);
      return;
    }
    scheduleWrite(grid);
  }, [scheduleWrite]);

  const onSave = useCallback(() => {
    submittingRef.current = true;
    flush();
  }, [flush]);

  useEffect(() => {
    const warnUnsaved = (event) => {
      if (autosaveRef.current || !dirtyRef.current || submittingRef.current) return;
      event.preventDefault();
      // Chrome/Edge before 119 only prompt for a non-empty returnValue.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
    };
    // A page restored from the back/forward cache keeps its state, including
    // a submittingRef left true by the Save that navigated away.
    const resetSubmitting = (event) => {
      if (event.persisted) submittingRef.current = false;
    };
    // pagehide too: bfcache navigations and mobile browsers don't reliably fire beforeunload.
    window.addEventListener('beforeunload', flush);
    window.addEventListener('beforeunload', warnUnsaved);
    window.addEventListener('pagehide', flush);
    window.addEventListener('pageshow', resetSubmitting);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('beforeunload', warnUnsaved);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('pageshow', resetSubmitting);
      flush();
    };
  }, [flush]);

  // Reads storage at click time: the grid may have changed or expired since mount.
  const handleLoad = () => {
    const current = readStoredGrid(snapshotKey, columns);
    if (!current) {
      setSaved(null);
      setOffer(null);
      return;
    }
    // eslint-disable-next-line no-alert
    if ((dirtyRef.current || pendingWrite.current) && !window.confirm(CONFIRM_LOAD)) return;
    // A debounced write of the grid being replaced must not overwrite the snapshot.
    dropPendingWrite();
    latestGrid.current = null;
    setLoaded(current.reconciled);
    setLoadCount((count) => count + 1);
    setNoticeDismissed(false);
    setSaved({ savedAt: current.savedAt, rowCount: current.reconciled.rowData.length });
    markDirty(false);
    setOffer(null);
  };

  // Also works with a grid open: cancel a pending autosave so it does not
  // store the grid again. dirty and the grid itself are left alone.
  const handleDiscard = () => {
    dropPendingWrite();
    removeSnapshot(snapshotKey);
    setSaved(null);
    setOffer(null);
  };

  const handleSaveNow = () => {
    if (!latestGrid.current || !write(latestGrid.current)) return false;
    markDirty(false);
    return true;
  };

  const handleAutosaveChange = (enabled) => {
    writeAutosavePreference(enabled);
    autosaveRef.current = enabled;
    setAutosave(enabled);
    if (enabled) {
      // If the write fails, stay dirty and let the unload flush retry.
      if (dirtyRef.current && !handleSaveNow()) pendingWrite.current = latestGrid.current;
    } else {
      flush();
    }
  };

  // Stable references matter: GridModeUrlBuilder re-initialises its data when
  // these change identity, which would wipe the user's edits.
  const gridData = useMemo(() => {
    if (!loaded) {
      return {
        field_columns: fieldColumns,
        initial_row_data: initialRowData,
        initial_pinned_row_data: initialPinnedRowData,
      };
    }
    const parentValue = (field) => {
      const cell = loaded.pinnedRowData[field];
      return (cell && cell.value) || '';
    };
    return {
      // Mirrors Terminus::ViewHelpers::GridMode#field_columns after a failed
      // save: parents get their value so their children show on mount.
      field_columns: fieldColumns.map((column) => (
        column.is_parent ? { ...column, current_value: parentValue(column.field) } : column
      )),
      initial_row_data: loaded.rowData,
      initial_pinned_row_data: loaded.pinnedRowData,
    };
  }, [loaded, fieldColumns, initialRowData, initialPinnedRowData]);

  const status = storageAvailable && (
    <SnapshotStatus
      offer={offer && { savedAt: offer.savedAt, rowCount: offer.reconciled.rowData.length }}
      onLoad={handleLoad}
      onDiscard={handleDiscard}
      autosave={autosave}
      onAutosaveChange={handleAutosaveChange}
      saved={saved}
      dirty={dirty}
      onSaveNow={handleSaveNow}
    />
  );

  return (
    <>
      {loaded && loaded.droppedCount > 0 && !noticeDismissed && (
        <DroppedValuesNotice
          count={loaded.droppedCount}
          onDismiss={() => setNoticeDismissed(true)}
        />
      )}
      <GridModeUrlBuilder
        key={loadCount}
        url_columns={urlColumns}
        field_columns={gridData.field_columns}
        other_columns={otherColumns}
        initial_row_data={gridData.initial_row_data}
        initial_pinned_row_data={gridData.initial_pinned_row_data}
        onGridChanged={onGridChanged}
        onSave={onSave}
        toolbarExtra={status}
      />
    </>
  );
}

GridModeUrlBuilderWithSnapshot.propTypes = {
  ...gridModeUrlBuilderPropTypes,
  snapshot_key: PropTypes.string.isRequired,
};
